import { PropsWithChildren } from "react";

import { getAmountLimitViolation } from "@utils/index";
import { Modal, Button, ConfirmModal } from "@components/index";
import AlertImg from "@assets/images/alert-triangle.svg";

const AlertContent = ({
  title,
  children,
}: { title?: string } & PropsWithChildren) => (
  <div className="flex flex-col items-center text-center">
    <img src={AlertImg} alt="Alert" className="h-12 w-12 md:h-14 md:w-14" />
    {title && (
      <div className="pt-2 text-400 font-bold text-black-100 md:pt-4 md:text-500">
        {title}
      </div>
    )}
    <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
      {children}
    </div>
  </div>
);

const SingleButton = ({
  title,
  onClick,
}: {
  title: string;
  onClick: () => void;
}) => (
  <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
    <Button
      type="button"
      variant="primary"
      title={title}
      className="mx-auto w-40"
      onClick={onClick}
    />
  </div>
);

export const DuplicatedTransactionModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => (
  <Modal show={show} onClose={onClose}>
    <AlertContent title="Duplicate Transaction Detected">
      It looks like you're trying to save a transaction with the same type,
      category, subcategory and amount as an existing one. To save this as a
      separate transaction, please add a unique description. This helps avoid
      confusion and ensures your records stay accurate.
    </AlertContent>
    <SingleButton title="Back to Editing" onClick={onClose} />
  </Modal>
);

export const AssetRequiredModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => (
  <Modal show={show} className="max-w-[520px]" onClose={onClose}>
    <AlertContent title="Asset field is required">
      Please select an existing asset or create a new one.
    </AlertContent>
    <div className="flex flex-col gap-y-4 pt-8 md:pt-12">
      <Button
        isLink
        title="Click here for detailed instructions"
        href="https://vimeo.com/1100639491"
        target="_blank"
        className="w-full"
      />
      <Button
        type="button"
        variant="secondary"
        title="Cancel"
        className="w-full"
        onClick={onClose}
      />
    </div>
  </Modal>
);

export const WrongAssetTypeModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => (
  <Modal show={show} onClose={onClose}>
    <AlertContent title="Transaction Not Allowed">
      You cannot create a <strong>Leased / HP / Loan transaction</strong>{" "}
      because the selected asset is not marked as{" "}
      <strong>Leased, Hire Purchase,</strong> or <strong>Loan</strong>.
      <br />
      Please update the asset details or select a valid asset before continuing.
    </AlertContent>
    <SingleButton title="Ok" onClick={onClose} />
  </Modal>
);

export const HardMaxAmountModal = ({
  show,
  message,
  onClose,
}: {
  show: boolean;
  message?: string;
  onClose: () => void;
}) => (
  <Modal show={show} onClose={onClose}>
    <AlertContent>
      {message || (
        <div>
          This exceeds the maximum value for this transaction type.
          <br />
          Please review the amount, if you believe it is correct please contact
          us.
        </div>
      )}
    </AlertContent>
    <SingleButton title="Ok" onClick={onClose} />
  </Modal>
);

type SoftAmountLimit = "softMax" | "softMin";

export const SoftAmountLimitModal = ({
  violation,
  softMaxMessage,
  softMinMessage,
  confirmButtonType = "button",
  onCancel,
  onConfirm,
}: {
  // Shown only for the soft limits; the hard maximum has its own modal
  violation: ReturnType<typeof getAmountLimitViolation>;
  softMaxMessage?: string;
  softMinMessage?: string;
  confirmButtonType?: "button" | "submit";
  onCancel: () => void;
  onConfirm: (confirmed: SoftAmountLimit) => void;
}) => (
  <ConfirmModal
    show={violation === "softMax" || violation === "softMin"}
    confirmButtonTitle="Save"
    confirmButtonType={confirmButtonType}
    cancelButtonTitle="Edit"
    text2={
      violation === "softMin"
        ? softMinMessage ||
          "This amount appears lower than we'd expect to see, is it correct?"
        : softMaxMessage ||
          "This amount appears higher than we'd expect to see, is it correct?"
    }
    onClickCancel={onCancel}
    onClickConfirm={() => onConfirm(violation as SoftAmountLimit)}
  />
);
